import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useFormContext } from 'react-hook-form';

import { useQuery } from '@apollo/client';

import CREDIT_COUNTERPART_SUMMARY, {
  CreditCounterpartSummaryProps,
  PayloadProps,
  ResponseProps,
} from '@graphql/query/operationLimits/credit/counterpartSummary';

import useRestriction from '~/hooks/useRestriction';
import formatNegotiationDeadlineLabel from '~/utils/formatNegotiationDeadlineLabel';

type CreditSummaryStatus = 'hidden' | 'loading' | 'empty' | 'error' | 'ready';
type CreditSummaryEvent = 'credit_summary_shown' | 'credit_summary_detail_click' | 'credit_summary_counterpart_recalculation';

interface CreditSummaryPayloadProps {
  operation: 'SALE' | 'PURCHASE';
  counterpartId?: string;
  counterpartName?: string;
  status?: Exclude<CreditSummaryStatus, 'hidden'>;
}

export interface UseCreditSummaryProps {
  status: CreditSummaryStatus;
  hasRequiredFields: boolean;
  counterpartId?: string;
  counterpartName?: string;
  group?: string;
  competence?: string;
  exposureMtm?: number;
  financialBalance?: number;
  priceReference: 'BBCE';
  handleOpenDetails: () => void;
}

const trackCreditSummaryEvent = (event: CreditSummaryEvent, payload: CreditSummaryPayloadProps) => {
  const dataLayerWindow = window as Window & { dataLayer?: Record<string, unknown>[] };

  window.dispatchEvent(new CustomEvent('storm:credit-summary', { detail: { event, ...payload } }));
  dataLayerWindow.dataLayer?.push({ event, ...payload });
};

function useCreditSummary(): UseCreditSummaryProps {
  const shownEvents = useRef<string[]>([]);
  const counterpartRef = useRef<string>();
  const { hasPermission } = useRestriction();
  const { watch } = useFormContext();
  const operation = watch('operation') as 'SALE' | 'PURCHASE';
  const buyerId = watch('buyerId') as string | undefined;
  const sellerId = watch('sellerId') as string | undefined;
  const buyerName = watch('buyerName') as string | undefined;
  const sellerName = watch('sellerName') as string | undefined;
  const canViewCreditSummary = hasPermission('OPERATION_LIMITS_CREDIT_SUMMARY');
  const counterpartId = operation === 'SALE' ? buyerId : sellerId;
  const fallbackCounterpartName = operation === 'SALE' ? buyerName : sellerName;
  const counterpartKey = counterpartId ? `${operation}:${counterpartId}` : undefined;
  const hasRequiredFields = Boolean(operation && counterpartId);
  const shouldFetch = Boolean(canViewCreditSummary && hasRequiredFields);

  const { data, loading, error } = useQuery<ResponseProps, PayloadProps>(CREDIT_COUNTERPART_SUMMARY, {
    skip: !shouldFetch,
    fetchPolicy: 'network-only',
    variables: {
      accountId: counterpartId!,
    },
  });

  const counterpart = useMemo<CreditCounterpartSummaryProps | undefined>(() => data?.creditCounterpartSummary, [data]);

  const status = useMemo<CreditSummaryStatus>(() => {
    if (!canViewCreditSummary) return 'hidden';
    if (!hasRequiredFields) return 'empty';
    if (loading) return 'loading';
    if (error) return 'error';
    if (!counterpart) return 'empty';

    return 'ready';
  }, [canViewCreditSummary, counterpart, error, hasRequiredFields, loading]);

  const counterpartName = counterpart?.counterpartName ?? fallbackCounterpartName;
  const group = counterpart?.rating;
  const competence = formatNegotiationDeadlineLabel(counterpart?.negotiationDeadlineLabel);

  const handleOpenDetails = useCallback(() => {
    trackCreditSummaryEvent('credit_summary_detail_click', {
      operation,
      counterpartId,
      counterpartName,
      status: status === 'hidden' ? undefined : status,
    });
  }, [counterpartId, counterpartName, operation, status]);

  useEffect(() => {
    if (!counterpartKey) return;

    if (counterpartRef.current && counterpartRef.current !== counterpartKey) {
      trackCreditSummaryEvent('credit_summary_counterpart_recalculation', {
        operation,
        counterpartId,
        counterpartName,
      });
    }

    counterpartRef.current = counterpartKey;
  }, [counterpartId, counterpartKey, counterpartName, operation]);

  useEffect(() => {
    if (!counterpartKey || status === 'hidden' || status === 'loading') return;

    if (shownEvents.current.includes(counterpartKey)) return;

    shownEvents.current.push(counterpartKey);

    trackCreditSummaryEvent('credit_summary_shown', {
      operation,
      counterpartId,
      counterpartName,
      status,
    });
  }, [counterpartId, counterpartKey, counterpartName, operation, status]);

  return {
    status,
    hasRequiredFields,
    counterpartId,
    counterpartName,
    group,
    competence,
    exposureMtm: counterpart?.exposureMtmFiltered,
    financialBalance: counterpart?.financialBalance,
    priceReference: 'BBCE',
    handleOpenDetails,
  };
}

export default useCreditSummary;
